import { Object as FabricObject } from 'fabric';

import { Position } from '../types/metaBullets';
import { useCanvasStore } from '../store/canvas';
import { generateObjectId } from '../utils/generateId';

import { TextEntity } from './TextEntity';

/**
 * Класс MetaBullet - виртуальная группа TextEntity объектов
 * НЕ является Fabric.js объектом, а только управляет группой TextEntity
 */
export class MetaBullet {
  // Уникальный ID метабуллита
  id: string;

  // ID canvas к которому принадлежит метабуллит
  canvasId: string;

  // Массив ID сущностей в порядке их отображения
  entityIds: string[] = [];

  // Конструктор
  constructor(canvasId: string, initialEntityId: string, predefinedId?: string) {
    this.id = predefinedId || generateObjectId(canvasId);
    this.canvasId = canvasId;
    this.entityIds = [initialEntityId];

    // console.debug('🎯 MetaBullet created:', {
    //   id: this.id,
    //   canvasId,
    //   initialEntityId,
    //   isPredefined: !!predefinedId,
    // });
  }

  /**
   * Получить все TextEntity сущности метабуллита с canvas
   */
  getEntities(): TextEntity[] {
    const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
    if (!canvas) {
      console.warn('⚠️ Canvas not found for MetaBullet:', this.canvasId);
      return [];
    }

    const entities = this.entityIds
      .map((id) => {
        const obj = canvas.getObjects().find((fabricObj: FabricObject) => fabricObj.id === id);
        return obj && TextEntity.isTextEntity(obj) ? obj : null;
      })
      .filter((obj): obj is TextEntity => obj !== null);

    // Сортируем по entityOrder для правильного порядка
    return entities.sort((a, b) => a.entityOrder - b.entityOrder);
  }

  /**
   * Добавить новую сущность в метабуллит
   * @param entity - TextEntity объект
   * @param insertIndex - Позиция вставки (по умолчанию в конец)
   */
  addEntity(entity: TextEntity, insertIndex?: number): void {
    // console.debug('➕ Adding entity to MetaBullet:', {
    //   metaBulletId: this.id,
    //   entityId: entity.id,
    //   insertIndex,
    // });

    // Устанавливаем связь с метабуллитом
    entity.metaBulletId = this.id;

    // Добавляем в нужную позицию
    if (insertIndex !== undefined && insertIndex >= 0 && insertIndex < this.entityIds.length) {
      this.entityIds.splice(insertIndex, 0, entity.id);
    } else {
      this.entityIds.push(entity.id);
    }

    // Пересчитываем порядок сущностей
    this.reorderEntities();

    // Пересчитываем позиции
    this.recalculatePositions(true);
  }

  /**
   * Удалить сущность из метабуллита
   * @param entityId - ID сущности для удаления
   */
  removeEntity(entityId: string): void {
    // console.debug('➖ Removing entity from MetaBullet:', {
    //   metaBulletId: this.id,
    //   entityId,
    // });

    const index = this.entityIds.indexOf(entityId);
    if (index > -1) {
      this.entityIds.splice(index, 1);

      // Если это была последняя сущность, метабуллит можно удалить
      if (this.entityIds.length === 0) {
        // console.debug('🗑️ MetaBullet is empty and can be removed:', this.id);
      } else {
        // Пересчитываем порядок и позиции
        this.reorderEntities();
        this.recalculatePositions(true);
      }
    }
  }

  /**
   * Пересчитать позиции всех сущностей в метабуллите
   * @param animated - Использовать анимацию при перемещении
   */
  async recalculatePositions(animated: boolean = true): Promise<void> {
    console.debug('🔄 Recalculating positions for MetaBullet:', this.id);
    const entities = this.getEntities();

    if (entities.length === 0) {
      // console.debug('📏 No entities to recalculate positions for MetaBullet:', this.id);
      return;
    }

    // console.debug('📏 Recalculating positions for MetaBullet:', {
    //   id: this.id,
    //   entitiesCount: entities.length,
    //   animated,
    // });

    // Сначала рассчитываем целевые позиции для всех сущностей
    const targetPositions: Array<{ entity: TextEntity; targetX: number; targetY: number }> = [];

    // Начинаем с Y координаты первой сущности
    let currentY = entities[0].top || 0;
    const baseX = entities[0].left || 0;

    // Рассчитываем целевые позиции для всех сущностей
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i];
      const targetY = currentY;

      // Для первой сущности X остается неизменным
      // Для остальных учитываем отступы слева (для буллитов)
      const targetX = i === 0 ? baseX : baseX + (entity.marginLeft || 0);

      targetPositions.push({
        entity,
        targetX,
        targetY,
      });

      // Вычисляем Y для следующей сущности
      currentY += (entity.height || 0) + (entity.marginBottom || 16);
    }

    // Теперь анимируем все сущности ПАРАЛЛЕЛЬНО
    if (animated) {
      const animationPromises = targetPositions.map(({ entity, targetX, targetY }) => {
        // Проверяем нужна ли анимация
        if (Math.abs(entity.top! - targetY) > 1 || Math.abs(entity.left! - targetX) > 1) {
          return this.animateEntityToPosition(entity, { x: targetX, y: targetY });
        } else {
          // Если анимация не нужна, просто устанавливаем позицию
          entity.set({ left: targetX, top: targetY });
          entity.setCoords();
          return Promise.resolve();
        }
      });

      // Ждем завершения ВСЕХ анимаций одновременно
      await Promise.all(animationPromises);
      // console.debug('🎉 All animations completed simultaneously for MetaBullet:', this.id);
    } else {
      // Без анимации - просто устанавливаем позиции
      targetPositions.forEach(({ entity, targetX, targetY }) => {
        entity.set({ left: targetX, top: targetY });
        entity.setCoords();
      });
    }

    // Обновляем canvas после всех изменений
    const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
    if (canvas) {
      canvas.requestRenderAll();

      // Восстанавливаем возможность создания бекапов для всех сущностей
      entities.forEach((entity) => entity.enableBackup());

      // Проверяем есть ли система MetaBulletSystem и в процессе ли инициализации
      // Если есть кастомное свойство _metaBulletSystem на canvas, используем его
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const system = canvas._metaBulletSystem;

      console.debug('🔄 Canvas:', canvas);

      if (system && system.isInInitialization) {
        // Во время инициализации только отмечаем что были изменения
        console.debug('🔄 Position changes during initialization - marking for later backup');
        system.markPositionChanges();
      } else {
        // Обычный режим - отправляем событие для создания бекапа
        canvas.fire('metaBullet:backup:needed', {
          metaBulletId: this.id,
          entitiesCount: entities.length,
          entityIds: this.entityIds,
          timestamp: Date.now(),
        });
      }
    }
  }

  /**
   * Пересчитать порядок сущностей (entityOrder) в метабуллите
   */
  private reorderEntities(): void {
    const entities = this.getEntities();

    entities.forEach((entity, index) => {
      entity.entityOrder = index;
    });

    // console.debug('🔢 Reordered entities in MetaBullet:', {
    //   metaBulletId: this.id,
    //   entities: entities.map((e) => ({ id: e.id, order: e.entityOrder })),
    // });
  }

  /**
   * Анимировать перемещение сущности в новую позицию
   * @param entity - TextEntity для анимации
   * @param position - Целевая позиция
   */
  private async animateEntityToPosition(entity: TextEntity, position: Position): Promise<void> {
    return new Promise((resolve) => {
      // Анимируем только если есть реальное изменение позиции
      const needsAnimation =
        Math.abs(entity.left! - position.x) > 1 || Math.abs(entity.top! - position.y) > 1;

      if (!needsAnimation) {
        resolve();
        return;
      }

      // console.debug('🎬 Animating entity position:', {
      //   entityId: entity.id,
      //   from: { x: entity.left, y: entity.top },
      //   to: position,
      // });

      // Используем нативную анимацию Fabric.js (исправляем easing тип)
      entity.animate(
        {
          top: position.y,
          left: position.x,
        },
        {
          duration: 300,
          // Используем функцию easing из fabric utils
          easing: (t: number, b: number, c: number, d: number) => {
            // easeOutCubic formula
            return c * ((t = t / d - 1) * t * t + 1) + b;
          },
          onChange: () => {
            const canvas = useCanvasStore.getState().getFabricInstance(this.canvasId);
            if (canvas) {
              canvas.requestRenderAll();
            }
          },
          onComplete: () => {
            entity.setCoords();
            // console.debug(
            //   '✅ Animation completed for entity:',
            //   entity.id,
            //   `(${Date.now() - startTime}ms)`,
            // );
            resolve();
          },
        },
      );
    });
  }

  /**
   * Получить информацию о метабуллите для отладки
   */
  getDebugInfo(): string {
    const entities = this.getEntities();
    return `MetaBullet[${this.id}] entities: ${entities.length}, ids: [${this.entityIds.join(', ')}]`;
  }
}
